import React, { createContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebaseConfig.js';
import { identifyUser } from '../utils/analytics';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);
      localStorage.setItem('token', idToken);
      return userCredential.user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        setToken(idToken);
        localStorage.setItem('token', idToken);
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        
        // Identify user in analytics whenever auth state changes
        identifyUser(firebaseUser);
      } else {
        setToken(null);
        localStorage.removeItem('token');
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add this new function to get auth headers
  const getAuthHeaders = async () => {
    // If there's a current user, get a fresh token
    if (auth.currentUser) {
      const freshToken = await auth.currentUser.getIdToken(true);
      // Remove this line to prevent state updates on every API call
      // setToken(freshToken); 
      localStorage.setItem('token', freshToken);
      return {
        'Authorization': `Bearer ${freshToken}`,
        'Content-Type': 'application/json'
      };
    }
    // If no user, return headers without authorization
    return {
      'Content-Type': 'application/json'
    };
  };

  const register = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      return userCredential.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);
      localStorage.setItem('token', idToken);
      
      // Identify user in analytics
      identifyUser(userCredential.user);
      
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setToken(null);
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      register,
      login,
      logout,
      resetPassword,
      signInWithGoogle,
      getAuthHeaders  
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
